import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useRef} from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {GetAnalysisTemplate} from 'analysis-tool/templates/graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import FieldTableRow, {DisablableControl} from 'registry/field/FieldTableRow';
import {AssaySchemaFieldTableRowProps} from 'results/assay-schemas/AssaySchemaFieldTableRow';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  generateRunFieldsSpecFromAnalysisTemplateVariables,
  type AnalysisTemplateVariableSchemaFieldSpec,
} from './AnalysisTemplateIntegrationUtils';

interface AnalysisTemplateVariableFieldTableProps {
  templateFields: Array<SchemaField | ClientSchemaField>;
  ownerId: string;
  ownerUrlId: string;
  onFieldsChange?: (fields: Array<ClientSchemaField>) => void;
  isPreview?: boolean;
}

interface QueryingAnalysisTemplateVariableFieldTableProps {
  analysisTemplateId: string | null;
  ownerId: string;
  ownerUrlId: string;
  onDraftFieldsChange: (fields: Array<ClientSchemaField>) => void;
  onAnalysisTemplateVariableMappingChange?: (mapping: Record<string, string>) => void;
  onError?: (error: string | null) => void;
  showDescription?: boolean; // Whether to show the description text above the table
  key?: string; // Add key prop to force remount when needed
}

/**
 * Helper function to create analysis template field table rows with configurable disabled controls
 */
function createAnalysisTemplateFieldTableRow(
  props: AssaySchemaFieldTableRowProps,
  disableEditingControls = false
): JSX.Element {
  const disabledMessage =
    "This field's property is derived from an automation template, so it cannot be modified";
  const temporarilyDisabledMessage = 'This field can be edited after the automation template is saved';
  // Get field options for the field definition dropdown
  const availableFieldOptions = getFieldOptions({
    initialField: props.initialField,
    schemaFieldSelectors: props.schemaFieldSelectors,
    storableSchemas: props.storableSchemas,
    assaySchemas: props.assaySchemas,
    tagSchemas: props.tagSchemas,
    schemaInterfaces: props.schemaInterfaces,
    includeJSONField: true,
    includeAssayLinks: true,
    includeBatchLinks: true,
    includeGenericBioentityLinks: true,
    includeBooleanField: true,
    includeArchivedSchemas: false,
    includeSnapshotField: false,
  });

  const baseDisabledControls = {
    [DisablableControl.DISPLAY_NAME_INPUT]: disabledMessage,
    [DisablableControl.ARCHIVE_BUTTON]: disabledMessage,
    [DisablableControl.DELETE_BUTTON]: disabledMessage,
    [DisablableControl.IS_MULTI_CHECKBOX]: disabledMessage,
    [DisablableControl.IS_REQUIRED_CHECKBOX]: disabledMessage,
    [DisablableControl.FIELD_DEFINITION_DROPDOWN]: disabledMessage,
  };

  const editingDisabledControls = disableEditingControls
    ? {
        [DisablableControl.WAREHOUSE_NAME_INPUT]: temporarilyDisabledMessage,
      }
    : {};

  return (
    <FieldTableRow
      {...props}
      availableFieldOptions={availableFieldOptions}
      disableControlsWithTooltips={{
        ...baseDisabledControls,
        ...editingDisabledControls,
      }}
      isInEditMode={false}
      areRemoveAndArchiveEnabled={false}
      disabled={disableEditingControls}
      isRemoveOnly={false}
      shouldShowRelaxationWarning={false}
      shouldShowWarehouseWarning={false}
      isFieldMigrationAvailable={false}
      shouldShowTooltipText={true}
      schemaId={props.schema?.id ?? null}
    />
  );
}

/**
 * Custom field table row for analysis template fields that disables specific controls
 * while allowing display name and warehouse name (system name) editing after the schema is saved
 */
function AnalysisTemplateFieldTableRow(props: AssaySchemaFieldTableRowProps): JSX.Element {
  return createAnalysisTemplateFieldTableRow(props, false);
}

/**
 * Preview version of the analysis template field table row that disables all controls
 * including display name and field definition editing
 */
function PreviewAnalysisTemplateFieldTableRow(props: AssaySchemaFieldTableRowProps): JSX.Element {
  return createAnalysisTemplateFieldTableRow(props, true);
}

/**
 * Converts generated field specs from analysis template variables to ClientSchemaField format
 * and builds the mapping from field system names to analysis variable IDs
 */
function convertFieldSpecsToClientFields(fieldSpecs: Array<AnalysisTemplateVariableSchemaFieldSpec>): {
  fields: Array<ClientSchemaField>;
  analysisVariableMapping: Record<string, string>;
} {
  const fields = fieldSpecs.map((spec, index) => {
    const fieldName = spec.name ?? `Automation template field ${index + 1}`;

    let requiredLink = null;
    if (spec.folderItemType) {
      requiredLink = {
        tagSchema: null,
        entitySchemaFieldPath: null,
        folderItemType: spec.folderItemType,
        schemaInterface: null,
      };
    }

    return {
      cid: `analysis-template-field-${index}`,
      name: fieldName,
      displayName: fieldName,
      systemName: spec.systemName,
      fieldType: spec.fieldType,
      isRequired: spec.isRequired,
      isComputed: false,
      isConvertedFromLink: false,
      isSnapshot: false,
      isIntegration: false,
      isMulti: spec.isMulti ?? false,
      isParentLink: false,
      description: null,
      tooltipText: null,
      archiveRecord: null,
      requiredLink,
    };
  });

  // Build the mapping: {schema_field_system_name: pipeline_variable_id}
  const analysisVariableMapping: Record<string, string> = {};
  fieldSpecs.forEach((spec) => {
    analysisVariableMapping[spec.systemName] = spec.variableId;
  });

  return {fields, analysisVariableMapping};
}

/**
 * Core component that displays fields generated from analysis template variables
 */
export function AnalysisTemplateVariableFieldTable({
  templateFields,
  ownerId,
  ownerUrlId,
  onFieldsChange,
  isPreview,
}: AnalysisTemplateVariableFieldTableProps): JSX.Element {
  // Enable editing when not in preview mode and has callback
  return (
    <div>
      <FieldTable
        key={templateFields.length === 0 ? 'empty' : templateFields[0].systemName}
        schemaId={null}
        schema={null}
        schemaHasData={false}
        isBeingCreated={false}
        isDisabled={true}
        itemClass={isPreview ? PreviewAnalysisTemplateFieldTableRow : AnalysisTemplateFieldTableRow}
        fields={templateFields as Array<ClientSchemaField>}
        onFieldsChange={onFieldsChange ?? noop} // Call parent callback when fields change
        schemaType="automation template"
        batchSchemas={[]}
        storableSchemas={[]}
        assaySchemas={[]}
        savingState={SavingState.COMPLETED}
        ownerUrlId={ownerUrlId}
        ownerId={ownerId}
        shouldShowIsRequired={true} // Show but disable via custom row component
        shouldShowIsIntegration={false}
        shouldShowIsParentLink={false}
        shouldShowMultiSelect={true} // Show but disable via custom row component
        shouldShowTooltipText={false}
        dataTestAttrs={{component: 'AnalysisTemplateVariableFieldTable', element: 'fieldTable'}}
        enforceUniqueDisplayName={false}
        title="Automation template fields"
        titleTooltip="Fields automatically generated from the selected analysis template variables. System names can be modified."
        shouldReplaceFieldsFromProps={true}
      />
    </div>
  );
}

/**
 * Component that queries for analysis template data and displays the field table
 */
export default function QueryingAnalysisTemplateVariableFieldTable({
  analysisTemplateId,
  ownerId,
  ownerUrlId,
  onDraftFieldsChange,
  onAnalysisTemplateVariableMappingChange,
  onError,
}: QueryingAnalysisTemplateVariableFieldTableProps): JSX.Element | null {
  const queryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    variables: {analysisTemplateId},
    skip: !analysisTemplateId,
  });

  const {queriedTemplateFields, analysisTemplateVariableMapping, error} = useMemo(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      const analysisTemplate = queryResult.data.analysisTemplate;
      assertIsAccessible(analysisTemplate);

      const result = generateRunFieldsSpecFromAnalysisTemplateVariables(
        analysisTemplate as AnalysisTemplateGraphQL
      );

      if (!result.success) {
        return {
          queriedTemplateFields: [],
          analysisTemplateVariableMapping: {},
          error: result.error,
        };
      }

      const {fields, analysisVariableMapping} = convertFieldSpecsToClientFields(result.fields);

      return {
        queriedTemplateFields: fields,
        analysisTemplateVariableMapping: analysisVariableMapping,
        error: undefined,
      };
    }
    return {queriedTemplateFields: [], analysisTemplateVariableMapping: {}, error: undefined};
  }, [queryResult]);

  // Necessary to prevent circular useEffect triggers since onDraftFieldsChange is defined each render
  const mountedRef = useRef(true);

  // Clear error on mount to handle template switching
  useEffect(() => {
    onError?.(null);
    mountedRef.current = true;
  }, [onError]);

  useEffect(() => {
    if (
      (queryResult.type === QueryResultType.SUCCESS || queryResult.type === QueryResultType.SKIPPED) &&
      mountedRef.current
    ) {
      onDraftFieldsChange(queriedTemplateFields);
      onAnalysisTemplateVariableMappingChange?.(analysisTemplateVariableMapping);
      onError?.(error ?? null);
      mountedRef.current = false;
    }
  }, [
    queryResult.type,
    onDraftFieldsChange,
    onAnalysisTemplateVariableMappingChange,
    onError,
    queriedTemplateFields,
    analysisTemplateVariableMapping,
    error,
  ]);

  if (queryResult.type !== QueryResultType.SUCCESS && queryResult.type !== QueryResultType.SKIPPED) {
    return (
      <div>
        <h4>Automation template fields</h4>
        <CenteredHorizontalThrobber />
      </div>
    );
  }

  // Return null if there's an error - error will be displayed by parent component
  if (error) {
    return null;
  }

  // Only render if there are fields to display
  if (queriedTemplateFields.length === 0) {
    return null;
  }

  return (
    <div>
      <p className={css(styles.description)}>
        To save the automation template fields, please save the schema. Once saved, system names may be
        edited.
      </p>
      <AnalysisTemplateVariableFieldTable
        templateFields={queriedTemplateFields}
        ownerId={ownerId}
        ownerUrlId={ownerUrlId}
        isPreview={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  description: {
    marginBottom: '8px',
    color: Color.GRAY_650,
    fontStyle: 'italic',
  },
});
